<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useField } from 'vee-validate';
import { computed, inject, onBeforeUnmount, ref, watch, type Ref } from 'vue';

import StandardSelect from '../StandardSelect.vue';
import VideoPlayer from '../VideoPlayer.vue';

import 'vue-lite-youtube-embed/style.css';
import { debounce } from 'lodash';

import { parseVideoId } from '@/helpers/video-utils';
import type { Locales, LocalizableString } from '@/services/types/api.types';

const { t } = useI18n();
const { getVideoId } = parseVideoId();

// PROPS
const props = defineProps<{
  name: string;
  label: string;
  language_enabled: Locales[];
  typeName?: string;
  videoType?: string;
  caption?: LocalizableString;
}>();

defineOptions({ inheritAttrs: false });

// COMPUTED
const nameFr = computed(() => `${props.name}.fr`);
const nameEn = computed(() => `${props.name}.en`);

const { value: valueFr, errorMessage: errorMessageFr } = useField<string | null>(nameFr.value);
const { value: valueEn, errorMessage: errorMessageEn } = useField<string | null>(nameEn.value);

const typeField = props.typeName ? useField<string>(props.typeName) : undefined;
const typeValue = typeField?.value;
const errorMessageVideoType = typeField?.errorMessage;

const videoOptions = ['Youtube', 'Vimeo'];
const defaultVideoType = 'Youtube';
const editLocale = inject<Ref<Locales>>('editLocale');

if (typeValue && typeValue.value == null) {
  typeValue.value = props.videoType ?? defaultVideoType;
}

const selectedVideoType = computed<string>(() => typeValue?.value ?? props.videoType ?? defaultVideoType);

const videoTypeSelected = computed(() => !props.typeName || !!typeValue?.value);

const activeVideoId = computed(() => (editLocale?.value === 'en' ? valueEn.value : valueFr.value));

const previewVideoId = ref<string | null>(activeVideoId.value);
const settlePreviewVideoId = debounce((videoId: string | null) => {
  previewVideoId.value = videoId;
}, 400);

watch(activeVideoId, (videoId) => settlePreviewVideoId(videoId));
onBeforeUnmount(() => settlePreviewVideoId.cancel());

// FUNCTIONS
function normalizeVideoId(url: string | number | null) {
  const videoId = getVideoId(typeof url === 'string' ? url : null, selectedVideoType.value);
  if (!videoId) return;
  if (editLocale?.value === 'en') {
    valueEn.value = videoId;
  } else if (editLocale?.value === 'fr') {
    valueFr.value = videoId;
  }
}

function resetVideoFields() {
  valueFr.value = '';
  valueEn.value = '';
}

function changeVideoType(newType: string) {
  if (!typeValue || newType === typeValue.value) return;
  typeValue.value = newType;
  resetVideoFields();
}
</script>

<template>
  <div class="localized-video-input row">
    <div class="localized-video-input__column">
      <standard-select
        v-if="props.typeName"
        :name="props.typeName"
        :label="t('form.dialogs.module.video.type')"
        :model-value="selectedVideoType"
        @update:model-value="changeVideoType"
        :options="videoOptions"
        emit-value
        outlined
        :error="!!errorMessageVideoType"
        :error-message="errorMessageVideoType"
      />
      <template v-if="videoTypeSelected">
        <q-input
          v-if="editLocale === 'fr'"
          v-model="valueFr"
          :label="`${props.label} (fr)`"
          v-bind="$attrs"
          :error="!!errorMessageFr"
          :error-message="errorMessageFr"
          outlined
          @update:modelValue="normalizeVideoId"
        />
        <q-input
          v-else-if="editLocale === 'en'"
          v-model="valueEn"
          :label="`${props.label} (en)`"
          v-bind="$attrs"
          :error="!!errorMessageEn"
          :error-message="errorMessageEn"
          outlined
          @update:modelValue="normalizeVideoId"
        />
      </template>
      <slot />
    </div>
    <div class="localized-video-input__column">
      <video-player
        v-if="videoTypeSelected && previewVideoId"
        :key="`${selectedVideoType}:${previewVideoId}`"
        :type="selectedVideoType"
        :video-id="previewVideoId"
        :title="props.caption ?? {}"
        :language-enabled="props.language_enabled"
      />
      <div v-else class="video-preview-placeholder" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.localized-video-input {
  gap: 1rem;
  margin-bottom: 20px;
}

.localized-video-input__column {
  flex: 1;
  min-width: 0;
}

.video-preview-placeholder {
  width: 100%;
  aspect-ratio: 16 / 9;
  background-color: #f5f5f5;
  border-radius: 4px;
}
</style>
